
import React from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { Eye, UserCircle, Ban } from "lucide-react";
import { Button } from "@/components/ui/button";
import { EnrichedVisitor } from "@/types/database";
import { toPascalCase } from "@/utils/text";
import { formatPageUrl } from "../utils/formatters";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";

interface VisitorsTableProps {
  visitors: EnrichedVisitor[];
  onViewVisitor: (visitor: EnrichedVisitor) => void;
  onBlockVisitor: (visitor: EnrichedVisitor) => void;
  selectedVisitors: Set<string>;
  onToggleVisitor: (id: string) => void;
  onToggleAll: () => void;
  allSelected: boolean;
  isLoading?: boolean;
}

const LoadingSkeleton = () => (
  <TableRow>
    <TableCell className="w-[50px]">
      <Skeleton className="h-4 w-4" />
    </TableCell>
    <TableCell>
      <div className="flex items-center gap-2">
        <Skeleton className="h-4 w-[120px]" />
        <Skeleton className="h-4 w-4 rounded-full" />
      </div>
    </TableCell>
    <TableCell><Skeleton className="h-4 w-[80px]" /></TableCell>
    <TableCell><Skeleton className="h-4 w-[100px]" /></TableCell>
    <TableCell><Skeleton className="h-4 w-[140px]" /></TableCell>
    <TableCell><Skeleton className="h-4 w-[120px]" /></TableCell>
    <TableCell><Skeleton className="h-4 w-[100px]" /></TableCell>
    <TableCell className="text-center">
      <div className="flex justify-center gap-2">
        <Skeleton className="h-9 w-[72px]" />
        <Skeleton className="h-9 w-[72px]" />
      </div>
    </TableCell>
  </TableRow>
);

const VisitorsTable = ({
  visitors,
  onViewVisitor,
  onBlockVisitor,
  selectedVisitors,
  onToggleVisitor,
  onToggleAll,
  allSelected,
  isLoading = false,
}: VisitorsTableProps) => {
  return (
    <div className="relative overflow-hidden">
      <ScrollArea className="w-full overflow-x-auto">
        <div className="min-w-[1200px]">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="w-[50px]">
                  <Checkbox 
                    checked={visitors.length > 0 && allSelected}
                    onCheckedChange={onToggleAll}
                    disabled={visitors.length === 0}
                  />
                </TableHead>
                <TableHead className="font-medium">Name</TableHead>
                <TableHead className="font-medium"># of employees</TableHead>
                <TableHead className="font-medium">Industries</TableHead>
                <TableHead className="font-medium">Location</TableHead>
                <TableHead className="font-medium">Visited Page</TableHead>
                <TableHead className="font-medium">Visit Time</TableHead>
                <TableHead className="font-medium text-center w-[120px]">AI Insights</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 5 }).map((_, index) => (
                  <LoadingSkeleton key={index} />
                ))
              ) : visitors.length > 0 ? (
                visitors.map((visitor) => {
                  const personName = visitor.person_name || visitor.person_data?.full_name;
                  const personJobTitle = visitor.person_job_title || visitor.person_data?.job_title;
                  const hasPersonData = personName && personJobTitle;
                  
                  return (
                    <TableRow 
                      key={visitor.id}
                      className="hover:bg-gray-50/60 transition-colors duration-200"
                    >
                      <TableCell className="w-[50px]">
                        <Checkbox 
                          checked={selectedVisitors.has(visitor.id)}
                          onCheckedChange={() => onToggleVisitor(visitor.id)}
                        />
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{toPascalCase(visitor.company_name) || '-'}</span>
                          {hasPersonData && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="inline-flex">
                                    <UserCircle className="h-4 w-4 text-blue-500" />
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <div className="text-xs">
                                    <p className="font-medium">{personName}</p>
                                    <p className="text-gray-500">{personJobTitle}</p>
                                  </div>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{visitor.company_size || '-'}</TableCell>
                      <TableCell>{toPascalCase(visitor.company_industry) || '-'}</TableCell>
                      <TableCell>
                        {visitor.location_locality && visitor.location_country ? (
                          <span className="flex items-center gap-1">
                            <span className="inline-block w-1.5 h-1.5 rounded-full bg-slate-400" />
                            {toPascalCase(visitor.location_locality)}, {toPascalCase(visitor.location_country)}
                          </span>
                        ) : '-'}
                      </TableCell>
                      <TableCell>
                        <span className="line-clamp-1 max-w-[150px]" title={visitor.page_url || '-'}>
                          {formatPageUrl(visitor.page_url)}
                        </span>
                      </TableCell>
                      <TableCell>
                        {new Date(visitor.created_at).toLocaleString('en-US', {
                          year: 'numeric',
                          month: 'numeric',
                          day: 'numeric',
                          hour: 'numeric',
                          minute: 'numeric',
                          hour12: true
                        })}
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex justify-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => onViewVisitor(visitor)}
                            className="gap-2 bg-[#1A1F2C] hover:bg-[#222222] transition-all duration-200 border-none text-white hover:text-white"
                          >
                            <Eye className="h-3.5 w-3.5" />
                            View
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => onBlockVisitor(visitor)}
                            className="gap-2 hover:bg-red-50 hover:text-red-600 transition-all duration-200"
                          >
                            <Ban className="h-3.5 w-3.5" />
                            Block
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-sm text-gray-500 py-4">
                    No verified company leads found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        <ScrollBar orientation="horizontal" />
      </ScrollArea>
      <div className="block md:hidden mt-2 text-xs text-gray-500 text-center">
        Swipe horizontally to see more data
      </div>
    </div>
  );
};

export default VisitorsTable;
